import React, { createContext, useState, useContext, useCallback } from 'react';
import fireControllerService from '../services/fireControllerService';

const FireControllerContext = createContext();

/**
 * FireControllerProvider component - Provides Fire Controller data and management functions
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} The context provider wrapper component
 */
export const FireControllerProvider = ({ children }) => {
  const [fireControllers, setFireControllers] = useState([]);
  const [selectedFireControllerId, setSelectedFireControllerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Initialize the Fire Controller service
   * @param {string} baseUrl - The Fire Bridge server base URL
   * @param {Function} getTokenFn - Function that returns the current authentication token
   */
  const initialize = useCallback((baseUrl, getTokenFn) => {
    try {
      fireControllerService.initialize(baseUrl, getTokenFn);
      console.log('FireControllerContext initialized');
    } catch (error) {
      console.error('Failed to initialize FireControllerContext:', error);
      setError(error);
      throw error;
    }
  }, []);

  /**
   * Fetch Fire Controller addresses for a user
   * @param {string} userId - The user ID
   */
  const fetchFireControllers = useCallback(async (userId) => {
    if (!userId) {
      console.warn('Cannot fetch Fire Controllers: userId is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const controllers = await fireControllerService.getFireControllerAddresses(userId);
      setFireControllers(controllers);

      if (controllers.length > 0 && !selectedFireControllerId) {
        setSelectedFireControllerId(controllers[0].id);
      }

      console.log(`Fetched ${controllers.length} associated Fire Controllers for user`);
    } catch (error) {
      console.error('Failed to fetch Fire Controllers:', error);
      setError(error);
      setFireControllers([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFireControllerId]);

  /**
   * Get the currently selected Fire Controller
   * @returns {Object|null} The selected Fire Controller or null if none selected
   */
  const getSelectedFireController = useCallback(() => {
    if (!selectedFireControllerId || fireControllers.length === 0) {
      return null;
    }
    return fireControllers.find(fc => fc.id === selectedFireControllerId) || null;
  }, [selectedFireControllerId, fireControllers]);

  /**
   * Select a Fire Controller by ID
   * @param {string} fireControllerId - The Fire Controller ID to select
   */
  const selectFireController = useCallback((fireControllerId) => {
    const controller = fireControllers.find(fc => fc.id === fireControllerId);
    if (controller) {
      setSelectedFireControllerId(fireControllerId);
      console.log(`Selected Fire Controller: ${fireControllerId}`);
    } else {
      console.warn(`Fire Controller not found: ${fireControllerId}`);
    }
  }, [fireControllers]);

  /**
   * Format address for display (street address only)
   * @param {Object} address - Address object
   * @returns {string} Formatted street address
   */
  const formatStreetAddress = useCallback((address) => {
    return fireControllerService.formatStreetAddress(address);
  }, []);

  /**
   * Clear all Fire Controller data
   */
  const clearFireControllers = useCallback(() => {
    setFireControllers([]);
    setSelectedFireControllerId(null);
    setError(null);
    console.log('Cleared Fire Controller data');
  }, []);

  return (
    <FireControllerContext.Provider
      value={{
        fireControllers,
        selectedFireControllerId,
        isLoading,
        error,
        initialize,
        fetchFireControllers,
        getSelectedFireController,
        selectFireController,
        formatStreetAddress,
        clearFireControllers,
      }}
    >
      {children}
    </FireControllerContext.Provider>
  );
};

/**
 * Custom hook to use the Fire Controller context
 * @returns {Object} The Fire Controller context object containing:
 * - fireControllers: Array of Fire Controller objects with id and address
 * - selectedFireControllerId: Currently selected Fire Controller ID
 * - isLoading: Boolean indicating if data is being fetched
 * - error: Error object if fetch failed
 * - initialize: Function to initialize the service with base URL and token getter
 * - fetchFireControllers: Function to fetch Fire Controllers for a user
 * - getSelectedFireController: Function to get the currently selected Fire Controller
 * - selectFireController: Function to select a Fire Controller by ID
 * - formatStreetAddress: Function to format an address for display
 * - clearFireControllers: Function to clear all Fire Controller data
 */
export const useFireController = () => {
  const context = useContext(FireControllerContext);

  if (!context) {
    throw new Error('useFireController must be used within a FireControllerProvider');
  }

  return context;
};

export default FireControllerContext;
